import { createContext, useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore/lite";
import { db } from "../Firebase";

export const ShopContext = createContext(null);

export const ShopContextProvider = (props) => {
  const [products, setProducts] = useState([
    {
      name: "",
      id: "",
      collection: "",
      family: "",
      materials: [],
      shortDescription: "",
      description: "",
      dimensions: { height: undefined, width: undefined, length: undefined },
      mass: undefined,
      size: "",
      variations: [
        {
          colour: "",
          stripeId: "",
          price: undefined,
          quantity: {
            inShop: 0,
            allocated: 0,
            inStock: 0,
            onOrder: 0,
          },
          imageSrc: [],
        },
      ],
    },
  ]);

  // Initialize cartItems by loading from localStorage synchronously
  const [cartItems, setCartItems] = useState(() => {
    const storedCart = localStorage.getItem("cartItems");
    return storedCart ? JSON.parse(storedCart) : {};
  });

  const [isCartOverlayVisible, setIsCartOverlayVisible] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState(null);

  // Save cart to localStorage whenever cartItems changes (no change here)
  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  const fetchProducts = async () => {
    const querySnapshot = await getDocs(collection(db, "products"));
    const newData = querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }));
    // Fetch live prices from Shopify
    const variantIds = newData.flatMap((product) =>
      product.variations.map(
        (variation) => `gid://shopify/ProductVariant/${variation.stripeId}`
      )
    );
    if (variantIds.length > 0) {
      const shopifyDomain = process.env.REACT_APP_SHOPIFY_DOMAIN;
      const storefrontAccessToken =
        process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
      if (!shopifyDomain || !storefrontAccessToken) {
        console.error("Shopify domain or Storefront API token is missing.");
      } else {
        const graphqlEndpoint = `https://${shopifyDomain}/api/2025-10/graphql.json`;
        const query = `
          query GetVariantPrices($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on ProductVariant {
                id
                price {
                  amount
                }
              }
            }
          }
        `;
        const variables = { ids: variantIds };
        try {
          const response = await fetch(graphqlEndpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
            },
            body: JSON.stringify({ query, variables }),
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const { data, errors } = await response.json();
          if (errors) {
            throw new Error(`GraphQL errors: ${JSON.stringify(errors)}`);
          }
          if (data && data.nodes) {
            const priceMap = new Map();
            data.nodes.forEach((node) => {
              if (node) {
                const idNumber = node.id.split("/").pop();
                priceMap.set(idNumber, parseFloat(node.price.amount));
              }
            });
            newData.forEach((product) => {
              product.variations.forEach((variation) => {
                const newPrice = priceMap.get(variation.stripeId);
                if (newPrice !== undefined) {
                  variation.price = newPrice;
                }
              });
            });
          }
        } catch (error) {
          console.error("Error fetching Shopify prices:", error);
        }
      }
    }
    setProducts(newData);
  };

  // Use useEffect to call fetchProducts when the component mounts
  useEffect(() => {
    fetchProducts();
  }, []); // The empty array ensures this only runs once when the component mounts

  const toggleCartOverlay = () => {
    setIsCartOverlayVisible(!isCartOverlayVisible);
  };

  const getTotalCartAmount = () => {
    let totalAmount = 0;
    // Iterate over each product in the cart
    for (const itemId in cartItems) {
      // Find the corresponding product in the products data
      const product = products.find((product) => product.id === itemId);
      if (product) {
        // Iterate over each variation in the cart item
        for (const variationIndex in cartItems[itemId]) {
          const quantity = cartItems[itemId][variationIndex].quantity;
          const variation = product.variations[variationIndex];
          // Ensure the variation exists and has a price
          if (variation && variation.price) {
            totalAmount += quantity * variation.price;
          }
        }
      }
    }
    return totalAmount;
  };

  const getTotalCartItemCount = () => {
    let itemCount = 0;
    for (const itemId in cartItems) {
      for (const variationIndex in cartItems[itemId]) {
        itemCount += cartItems[itemId][variationIndex].quantity;
      }
    }
    return itemCount;
  };

  const addToCart = (product, variationIndex, quantityToAdd) => {
    const itemId = product.id;
    const variationId = variationIndex;
    setLastAddedItem({
      itemId,
      variationIndex,
      quantityToAdd,
      priceId: variationId,
    });
    setCartItems((prev) => {
      const itemExists = prev[itemId];
      const variationExists = itemExists
        ? itemExists[variationId] != null
        : false;
      // If the item and variation exist, update the quantity
      if (variationExists) {
        return {
          ...prev,
          [itemId]: {
            ...prev[itemId],
            [variationId]: {
              ...prev[itemId][variationId],
              quantity: prev[itemId][variationId].quantity + quantityToAdd,
            },
          },
        };
      } else {
        // If the item exists but not the variation, add the new variation
        if (itemExists) {
          return {
            ...prev,
            [itemId]: {
              ...prev[itemId],
              [variationId]: {
                quantity: quantityToAdd,
              },
            },
          };
        } else {
          // If the item does not exist, add the item and the variation
          return {
            ...prev,
            [itemId]: {
              [variationId]: {
                quantity: quantityToAdd,
              },
            },
          };
        }
      }
    });
  };

  const removeFromCart = (itemId, variationIndex) => {
    setCartItems((prev) => {
      const updatedCart = { ...prev };
      if (updatedCart[itemId] && updatedCart[itemId][variationIndex]) {
        if (updatedCart[itemId][variationIndex].quantity > 1) {
          updatedCart[itemId][variationIndex].quantity -= 1;
        } else {
          // If the quantity is 1, remove the variation entirely
          delete updatedCart[itemId][variationIndex];
          // If there are no more variations left for the product, remove the product as well
          if (Object.keys(updatedCart[itemId]).length === 0) {
            delete updatedCart[itemId];
          }
        }
      }
      return updatedCart;
    });
  };

  const updateCartItemCount = (itemId, variationIndex, newAmount) => {
    setCartItems((prev) => {
      const updatedCart = { ...prev };
      if (newAmount > 0) {
        // If the new amount is greater than 0, update the quantity
        if (updatedCart[itemId] && updatedCart[itemId][variationIndex]) {
          updatedCart[itemId][variationIndex].quantity = newAmount;
        } else {
          // If the product or variation does not exist, add it
          if (!updatedCart[itemId]) {
            updatedCart[itemId] = {};
          }
          updatedCart[itemId][variationIndex] = { quantity: newAmount };
        }
      } else {
        // If the new amount is 0 or less, remove the variation
        if (updatedCart[itemId] && updatedCart[itemId][variationIndex]) {
          delete updatedCart[itemId][variationIndex];
          // If there are no more variations left for the product, remove the product as well
          if (Object.keys(updatedCart[itemId]).length === 0) {
            delete updatedCart[itemId];
          }
        }
      }
      return updatedCart;
    });
  };

  const checkout = () => {
    setCartItems({});
    // Optionally clear localStorage here too if you want to reset after checkout
    localStorage.removeItem("cartItems");
  };

  const contextValue = {
    cartItems,
    addToCart,
    updateCartItemCount,
    removeFromCart,
    getTotalCartAmount,
    getTotalCartItemCount,
    checkout,
    isCartOverlayVisible,
    toggleCartOverlay,
    lastAddedItem,
    products,
  };

  return (
    <ShopContext.Provider value={contextValue}>
      {props.children}
    </ShopContext.Provider>
  );
};
